/**
 * Схема повреждений: промтоварный фургон (будка) на шасси ГАЗель.
 *
 * ── Почему так устроено ───────────────────────────────────────────
 *
 * Легковая схема «седан» коммерческому транспорту не подходит:
 * зоны риска другие. У будки страдают верхние кромки (арки, ветки,
 * козырьки), ворота и запоры, зеркала на выносных кронштейнах,
 * подножки кабины и противоподкатный брус. Всё это здесь —
 * отдельные зоны, потому что «вмятина где-то на машине» спор
 * не решает, а «вмятина на верхней кромке слева» решает.
 *
 * ── Идентификаторы ────────────────────────────────────────────────
 *
 * ZONE ID МЕНЯТЬ НЕЛЬЗЯ. Он лежит в базе (car_damages.zone_id,
 * trip_damage_marks.zone_id) у всех уже зафиксированных повреждений.
 * Переименование id обнулит историю кузова: старые точки перестанут
 * находиться на схеме. Добавлять новые зоны можно свободно.
 *
 * ── Одна деталь — один id в разных проекциях ──────────────────────
 *
 * Зеркало видно и спереди, и сбоку; крыша кабины — спереди и сверху;
 * верхняя кромка будки — с торца и сверху. Это одна физическая
 * деталь, поэтому id общий, а прямоугольник свой в каждой проекции.
 * Иначе одна вмятина попадала бы в базу под двумя разными зонами
 * в зависимости от того, с какой стороны водитель её заметил.
 *
 * ── Система координат ─────────────────────────────────────────────
 *
 * Все проекции живут в едином viewBox 480×320 и в одном масштабе —
 * требование консистентности контейнера из брифа. Точка повреждения
 * хранится нормализованной (0..1) внутри проекции: это контракт
 * с бэкендом и он не зависит от того, как схема отрисована.
 */

export const VIEWBOX = { width: 480, height: 320 }

// Зоны тоньше этого (в единицах viewBox) считаются тонкими и при
// промахе добираются инфляцией: кромку и запор пальцем в перчатке
// иначе не поймать. Порог соответствует ~28 px при типичной ширине
// схемы на телефоне.
export const THIN_THRESHOLD = 28

const z = (id, label, material, x, y, w, h) => ({
  id, label, material, rect: { x, y, w, h },
})

// ── Перед ───────────────────────────────────────────────────────────
// Будка шире и выше кабины, поэтому сверху и по бокам видна она.
const FRONT = [
  z('box_top_edge_front', 'Верхняя кромка будки (перед)', 'box_panel', 60, 18, 360, 26),
  z('box_wall_front', 'Передняя стенка будки', 'box_panel', 60, 44, 360, 24),
  z('cab_roof', 'Крыша кабины', 'metal', 112, 62, 256, 18),
  z('cab_windshield', 'Лобовое стекло', 'glass', 130, 80, 220, 56),
  z('cab_mirror_left', 'Зеркало левое (на кронштейне)', 'plastic', 74, 88, 38, 54),
  z('cab_mirror_right', 'Зеркало правое (на кронштейне)', 'plastic', 368, 88, 38, 54),
  z('cab_hood', 'Капот', 'metal', 130, 136, 220, 16),
  z('cab_headlight_left', 'Фара левая', 'glass', 114, 152, 36, 32),
  z('cab_headlight_right', 'Фара правая', 'glass', 330, 152, 36, 32),
  z('cab_grille', 'Решётка радиатора', 'plastic', 152, 152, 176, 32),
  z('cab_bumper_front', 'Бампер передний', 'plastic', 106, 184, 268, 30),
  z('cab_step_left', 'Подножка левая', 'metal', 112, 214, 44, 22),
  z('cab_step_right', 'Подножка правая', 'metal', 324, 214, 44, 22),
  z('wheel_front_left', 'Колесо переднее левое', 'wheel', 84, 228, 46, 58),
  z('wheel_front_right', 'Колесо переднее правое', 'wheel', 350, 228, 46, 58),
]

// ── Зад ─────────────────────────────────────────────────────────────
// Главное место споров: ворота, запоры и то, чем машина цепляет
// рампу. Спарка вынесена сюда — с торца видно оба колеса оси.
const REAR = [
  z('box_top_edge_rear', 'Верхняя кромка будки (зад)', 'box_panel', 50, 18, 380, 26),
  z('box_gate_left', 'Створка ворот левая', 'box_panel', 50, 44, 186, 188),
  z('box_gate_right', 'Створка ворот правая', 'box_panel', 244, 44, 186, 188),
  z('box_gate_locks', 'Запоры ворот', 'metal', 228, 56, 24, 164),
  z('box_gate_seal', 'Уплотнитель ворот', 'plastic', 50, 226, 380, 14),
  z('light_rear_left', 'Фонарь задний левый', 'plastic', 58, 244, 42, 26),
  z('light_rear_right', 'Фонарь задний правый', 'plastic', 380, 244, 42, 26),
  z('chassis_underrun_bar', 'Противоподкатный брус', 'metal', 72, 278, 336, 18),
  z('wheel_rear_left_outer', 'Заднее левое внешнее (спарка)', 'wheel', 96, 240, 40, 54),
  z('wheel_rear_left_inner', 'Заднее левое внутреннее (спарка)', 'wheel', 138, 240, 34, 54),
  z('wheel_rear_right_outer', 'Заднее правое внешнее (спарка)', 'wheel', 344, 240, 40, 54),
  z('wheel_rear_right_inner', 'Заднее правое внутреннее (спарка)', 'wheel', 308, 240, 34, 54),
]

// Гидроборт есть не у всех машин — зона подмешивается по флагу.
// Кладём поверх ворот: платформа в сложенном виде их и закрывает.
const TAIL_LIFT = [
  z('tail_lift_platform', 'Гидроборт — платформа', 'metal', 118, 232, 244, 40),
  z('tail_lift_cylinder', 'Гидроборт — цилиндры', 'metal', 96, 250, 22, 46),
]

// ── Задние модули других кузовов ─────────────────────────────────────
// Кабина у линейки общая, поэтому меняется только задняя часть
// и борта. Материал зоны решает, какие дефекты предложат водителю:
// у тента «порез», у сэндвич-панели «пробой», у борта «деформация».

const REAR_TENT = [
  z('tent_top_edge_rear', 'Верхняя кромка тента (зад)', 'tent', 50, 18, 380, 26),
  z('tent_rear_panel', 'Задний полог тента', 'tent', 50, 44, 380, 170),
  z('tent_eyelets_rear', 'Люверсы задние', 'tent', 50, 214, 380, 16),
  z('tent_straps_rear', 'Ремни крепления', 'tent', 120, 230, 240, 14),
  z('flatbed_gate_rear', 'Задний борт (откидной)', 'metal', 60, 232, 360, 40),
  z('light_rear_left', 'Фонарь задний левый', 'plastic', 58, 244, 42, 26),
  z('light_rear_right', 'Фонарь задний правый', 'plastic', 380, 244, 42, 26),
  z('chassis_underrun_bar', 'Противоподкатный брус', 'metal', 72, 278, 336, 18),
  z('wheel_rear_left_outer', 'Заднее левое внешнее (спарка)', 'wheel', 96, 240, 40, 54),
  z('wheel_rear_left_inner', 'Заднее левое внутреннее (спарка)', 'wheel', 138, 240, 34, 54),
  z('wheel_rear_right_outer', 'Заднее правое внешнее (спарка)', 'wheel', 344, 240, 40, 54),
  z('wheel_rear_right_inner', 'Заднее правое внутреннее (спарка)', 'wheel', 308, 240, 34, 54),
]

const REAR_FLATBED = [
  z('flatbed_gate_rear', 'Задний борт (откидной)', 'metal', 50, 150, 380, 82),
  z('flatbed_gate_locks', 'Запоры заднего борта', 'metal', 228, 160, 24, 62),
  z('flatbed_deck', 'Настил платформы', 'metal', 50, 132, 380, 18),
  z('light_rear_left', 'Фонарь задний левый', 'plastic', 58, 244, 42, 26),
  z('light_rear_right', 'Фонарь задний правый', 'plastic', 380, 244, 42, 26),
  z('chassis_underrun_bar', 'Противоподкатный брус', 'metal', 72, 278, 336, 18),
  z('wheel_rear_left_outer', 'Заднее левое внешнее (спарка)', 'wheel', 96, 240, 40, 54),
  z('wheel_rear_left_inner', 'Заднее левое внутреннее (спарка)', 'wheel', 138, 240, 34, 54),
  z('wheel_rear_right_outer', 'Заднее правое внешнее (спарка)', 'wheel', 344, 240, 40, 54),
  z('wheel_rear_right_inner', 'Заднее правое внутреннее (спарка)', 'wheel', 308, 240, 34, 54),
]

// Цельнометаллический фургон: кузов и кабина — одно целое, поэтому
// сзади распашные или подъёмные двери, а не ворота будки.
const REAR_PANEL_VAN = [
  z('van_top_edge_rear', 'Верхняя кромка кузова (зад)', 'metal', 50, 18, 380, 26),
  z('van_door_left', 'Задняя дверь левая', 'metal', 50, 44, 186, 188),
  z('van_door_right', 'Задняя дверь правая', 'metal', 244, 44, 186, 188),
  z('van_door_glass_left', 'Стекло задней двери левое', 'glass', 74, 64, 130, 60),
  z('van_door_glass_right', 'Стекло задней двери правое', 'glass', 268, 64, 130, 60),
  z('van_door_locks', 'Замки задних дверей', 'metal', 228, 56, 24, 164),
  z('van_bumper_rear', 'Бампер задний', 'plastic', 50, 232, 380, 30),
  z('light_rear_left', 'Фонарь задний левый', 'plastic', 58, 244, 42, 26),
  z('light_rear_right', 'Фонарь задний правый', 'plastic', 380, 244, 42, 26),
  z('wheel_rear_left_outer', 'Колесо заднее левое', 'wheel', 100, 244, 46, 52),
  z('wheel_rear_right_outer', 'Колесо заднее правое', 'wheel', 334, 244, 46, 52),
]

/**
 * Борта и крыша с подменой материала и подписи.
 *
 * Геометрия кабины и грузового отсека у линейки одинаковая —
 * различается только то, из чего сделан кузов. Отрисовывать четыре
 * почти одинаковые таблицы вручную значит гарантированно развести
 * их при первой правке, поэтому меняются только материал, префикс
 * идентификатора и слово в подписи.
 */
const bodySkin = {
  box: { key: 'box', material: 'box_panel', noun: 'будки', dualRear: true },
  tent: { key: 'tent', material: 'tent', noun: 'тента', dualRear: true },
  // У цельнометаллического фургона ошиновка односкатная —
  // «спарки» в подписи быть не должно, иначе водитель ищет
  // на схеме второе колесо, которого нет.
  panel_van: { key: 'van', material: 'metal', noun: 'кузова', dualRear: false },
  flatbed: { key: 'flatbed', material: 'metal', noun: 'платформы', dualRear: true },
}

const sideZonesFor = (side, skin) => {
  const S = side === 'left' ? 'left' : 'right'
  const m = side === 'left' ? 'левый' : 'правый'
  const f = side === 'left' ? 'левая' : 'правая'
  const n = side === 'left' ? 'левое' : 'правое'
  const g = side === 'left' ? 'левого' : 'правого'
  const K = skin.key
  return [
    z(`${K}_top_edge_${S}`, `Верхняя кромка ${skin.noun} (${m} борт)`, skin.material, 150, 38, 292, 18),
    z(`${K}_panel_${S}`, `Борт ${skin.noun} ${m}`, skin.material, 150, 56, 292, 158),
    z(`${K}_rail_${S}`, `Обвязка ${g} борта`, 'metal', 150, 214, 292, 20),
    z(`cab_mirror_${S}`, `Зеркало ${n} (на кронштейне)`, 'plastic', 34, 54, 38, 50),
    z(`cab_door_${S}`, `Дверь кабины ${f}`, 'metal', 60, 70, 90, 134),
    z(`cab_window_${S}`, `Стекло двери ${n}`, 'glass', 72, 80, 66, 44),
    z(`cab_fender_${S}`, `Крыло кабины ${n}`, 'metal', 24, 118, 36, 86),
    z(`cab_step_${S}`, `Подножка ${f}`, 'metal', 62, 204, 84, 20),
    z(`wheel_front_${S}`, `Колесо переднее ${n}`, 'wheel', 54, 212, 62, 62),
    z(`wheel_rear_${S}_outer`,
      skin.dualRear ? `Заднее ${n} внешнее (спарка)` : `Колесо заднее ${m === 'левый' ? 'левое' : 'правое'}`,
      'wheel', 300, 212, 62, 62),
  ]
}

const topZonesFor = (skin) => {
  const K = skin.key
  return [
    z('cab_roof', 'Крыша кабины', 'metal', 44, 92, 116, 136),
    z(`${K}_roof`, `Крыша ${skin.noun}`, skin.material, 176, 84, 250, 152),
    z(`${K}_top_edge_front`, `Верхняя кромка ${skin.noun} (перед)`, skin.material, 160, 84, 16, 152),
    z(`${K}_top_edge_rear`, `Верхняя кромка ${skin.noun} (зад)`, skin.material, 426, 84, 16, 152),
    z(`${K}_top_edge_left`, `Верхняя кромка ${skin.noun} (левый борт)`, skin.material, 176, 68, 250, 16),
    z(`${K}_top_edge_right`, `Верхняя кромка ${skin.noun} (правый борт)`, skin.material, 176, 236, 250, 16),
  ]
}

// Передняя проекция общая: спереди виден в основном кабинный модуль.
const frontFor = (skin) => [
  z(`${skin.key}_top_edge_front`, `Верхняя кромка ${skin.noun} (перед)`, skin.material, 60, 18, 360, 26),
  z(`${skin.key}_wall_front`, `Передняя стенка ${skin.noun}`, skin.material, 60, 44, 360, 24),
  ...FRONT.slice(2),
]

const REAR_BY_TYPE = {
  box: REAR,
  tent: REAR_TENT,
  panel_van: REAR_PANEL_VAN,
  flatbed: REAR_FLATBED,
}

/**
 * Проекции схемы.
 *
 * Порядок — как водитель обходит машину: от кабины назад, потом
 * борта, потом крыша. Крыша последняя, потому что на неё смотрят
 * реже всего и только когда есть повод.
 */
export function truckProjections(bodyType, { hasTailLift = false } = {}) {
  const skin = bodySkin[bodyType]
  if (!skin) return null

  // Платформа без бортов сверху не осматривается как кузов —
  // там настил, а не крыша, и он уже есть в задней проекции.
  const rear = REAR_BY_TYPE[bodyType]
  return [
    { key: 'front', label: 'Перед', zones: frontFor(skin) },
    { key: 'rear', label: 'Зад', zones: hasTailLift ? [...rear, ...TAIL_LIFT] : rear },
    { key: 'left', label: 'Левый борт', zones: sideZonesFor('left', skin) },
    { key: 'right', label: 'Правый борт', zones: sideZonesFor('right', skin) },
    { key: 'top', label: bodyType === 'flatbed' ? 'Платформа' : 'Крыша', zones: topZonesFor(skin) },
  ]
}

/** Промтоварный фургон — исторически первый поддержанный кузов. */
export function boxTruckProjections(opts) {
  return truckProjections('box', opts)
}

/** Плоский список зон — для поиска по id (например, при показе старой точки). */
export function allZones(opts, bodyType = 'box') {
  // Здесь умолчание безопасно: это справочник для поиска метки,
  // а не источник схемы для водителя.
  const seen = new Map()
  for (const p of truckProjections(bodyType, opts) || []) {
    for (const zone of p.zones) if (!seen.has(zone.id)) seen.set(zone.id, zone)
  }
  return [...seen.values()]
}

export function zoneLabel(zoneId, opts, bodyType = 'box') {
  return allZones(opts, bodyType).find((zn) => zn.id === zoneId)?.label || zoneId
}

export const SUPPORTED_BODY_TYPES = Object.keys(bodySkin)
